import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import type { SortDirection } from "#app/lib/data";

import { AGE_STATUS, AGE_STATUS_CONFIG } from "#app/lib/age-status";

/**
 * The "no value" group sentinel. Mirrors the component library's
 * `UNASSIGNED_GROUP_KEY` AND the core `issueGroupCounts` resolver, which both
 * emit this exact string for null group keys. Re-declared here (not imported)
 * because this module is pure-logic and runs in the node vitest harness, which
 * cannot load the component library. The value is a fixed
 * cross-system contract — keep these three in sync.
 */
export const UNASSIGNED_GROUP_KEY = "__unassigned__";

// Shared issue-grouping selectors — the single source of truth for how a
// grouping dimension + group key map to (a) a GraphQL `where` selector and (b)
// the server `groupBy` field that feeds `useIssueGroupCounts`. Consumed by both
// the issues table and the workflow board so the two screens can never drift.
// Pure (no React / Apollo) so it unit-tests in the node vitest runner.

/**
 * The dimensions an issue screen can group by. `assignee` is USERS-ONLY — a
 * team is its own dimension (`team`), resolved server-side via the
 * `assigneeTeamIds` membership filter, never by stuffing a team id into the
 * assignee entityId list (a team id is not a user entityId, so that never
 * matched). A single-workflow board groups by `stage`; a cross-workflow board
 * (a team or member) groups by `workflow`. The table adds `issueType`.
 */
export type IssueGroupDimension =
  | "stage"
  | "workflow"
  | "assignee"
  | "team"
  // The DIRECT Teams property (issue_team) — "which team owns this", vs
  // `team`'s assignee-membership expansion ("whose members are assigned").
  | "teams"
  | "age"
  | "issueType";

/**
 * The server `groupBy` field for each dimension — the allowlist the core
 * `issueGroupCounts` resolver accepts (`stageId | issueTypeId | workflowId |
 * age.status | assignees.entityId | team | teams`). Feeds
 * `useIssueGroupCounts`.
 */
export const ISSUE_GROUP_SERVER_KEY: Record<IssueGroupDimension, string> = {
  stage: "stageId",
  workflow: "workflowId",
  assignee: "assignees.entityId",
  team: "team",
  teams: "teams",
  age: "age.status",
  issueType: "issueTypeId",
};

/**
 * Build the GraphQL `where` selector for one group of a dimension. `key` is the
 * group's id (a stageId / entityId / teamId / age status / issueTypeId) or
 * {@link UNASSIGNED_GROUP_KEY} for the "no value" bucket — the same sentinel the
 * server emits for null keys in `issueGroupCounts`.
 */
export function buildGroupWhere(
  dimension: IssueGroupDimension,
  key: string,
): Record<string, unknown> {
  const unassigned = key === UNASSIGNED_GROUP_KEY;
  switch (dimension) {
    case "stage":
      return unassigned
        ? { stageId: { exists: false } }
        : { stageId: { eq: key } };
    case "workflow":
      return unassigned
        ? { workflowId: { exists: false } }
        : { workflowId: { eq: key } };
    case "assignee":
      return unassigned
        ? { assignees: { entityId: { isEmpty: true } } }
        : { assignees: { entityId: { hasSome: [key] } } };
    case "team":
      return unassigned
        ? { assigneeTeamIds: { isEmpty: true } }
        : { assigneeTeamIds: { hasSome: [key] } };
    case "teams":
      return unassigned
        ? { teamIds: { isEmpty: true } }
        : { teamIds: { hasSome: [key] } };
    case "age":
      return unassigned
        ? { age: { status: { in: [null] } } }
        : { age: { status: { eq: key } } };
    case "issueType":
      return unassigned
        ? { issueTypeId: { exists: false } }
        : { issueTypeId: { eq: key } };
  }
}

/**
 * The follow-up grouping buckets, in display order. Labels come from
 * `AGE_STATUS_CONFIG` — restating them here is how a band ends up reading "Off
 * Track" beside a card chip that reads "Stale". The unassigned bucket IS
 * updates-off: the server has no fourth status, a Project with updates off
 * carries a null status, so `disabled` (the client's sentinel for that
 * absence) can never key a bucket of its own — a column filtering on it
 * would sit at zero forever while every such Project landed in the null one.
 */
export const AGE_GROUPS: ReadonlyArray<{ key: string; label: string }> = [
  { key: AGE_STATUS.onTrack, label: AGE_STATUS_CONFIG.onTrack.label },
  { key: AGE_STATUS.atRisk, label: AGE_STATUS_CONFIG.atRisk.label },
  { key: AGE_STATUS.offTrack, label: AGE_STATUS_CONFIG.offTrack.label },
  { key: UNASSIGNED_GROUP_KEY, label: AGE_STATUS_CONFIG.disabled.label },
];

// ---------------------------------------------------------------------------
// Group by — the widget-table picker vocabulary
// ---------------------------------------------------------------------------

/**
 * The dimensions a widget issue table can group by, plus the cleared state.
 * The full-page views group by scope (a workflow board is stage-grouped, a
 * team board workflow-grouped); the widget tables let the viewer pick, and
 * this union is that picker's vocabulary.
 */
export type IssueTableGroupBy =
  | "none"
  | Extract<IssueGroupDimension, "stage" | "workflow" | "age" | "issueType">;

/**
 * The picker options for {@link IssueTableGroupBy}, in display order. One list
 * feeds every group-by picker over a widget issue table (the toolbar chip and
 * the settings select) so the offered dimensions cannot drift per surface.
 */
export const ISSUE_TABLE_GROUP_BY_OPTIONS: ReadonlyArray<{
  value: IssueTableGroupBy;
  label: string;
}> = [
  { value: "none", label: "None" },
  { value: "stage", label: "Stage" },
  { value: "workflow", label: "Workflow" },
  { value: "age", label: "Follow up" },
  { value: "issueType", label: "Project type" },
];

/** Display label for a group-by value (`"issueType"` → "Project type"). */
export function issueTableGroupByLabel(value: IssueTableGroupBy): string {
  return (
    ISSUE_TABLE_GROUP_BY_OPTIONS.find((option) => option.value === value)?.label ?? "None"
  );
}

// ---------------------------------------------------------------------------
// Sort by — the Customize view row order
// ---------------------------------------------------------------------------

/**
 * The fields an issue view can sort by, keyed by the core `issues(orderBy:)`
 * key each one sends. Shared by the list (table order) and the board
 * (within-column card order) so the two views offer the same vocabulary.
 * `none` is the cleared state — the natural order: a list's default
 * creation-date order, a board column's manual order.
 */
export const ISSUE_SORT_LABELS: Record<string, string> = {
  none: "None",
  name: "Name",
  stage: "Stage",
  assignee: "Assignee",
  startDate: "Start Date",
  targetDate: "Target Date",
  creationDate: "Date Created",
  lastUpdateDate: "Last Updated",
  ageDate: "Follow up",
};

/**
 * Icons for {@link ISSUE_SORT_LABELS}, matching the group-by pickers where the
 * two share a field. Typed as plain strings (validated at the call sites,
 * which pass them to `IconName`-typed props) so this module stays free of
 * runtime library imports — it runs in the node vitest harness.
 */
export const ISSUE_SORT_ICONS = {
  name: "a-large-small",
  stage: PROPERTY_ICON.stage,
  assignee: PROPERTY_ICON.assignee,
  startDate: "calendar-clock",
  targetDate: "calendar-check",
  creationDate: "calendar",
  lastUpdateDate: "history",
  ageDate: PROPERTY_ICON.followUp,
} as const;

/**
 * The direction a field starts on when it becomes the sort — its natural
 * reading, so the first press gives the order a person almost always wants:
 * event dates newest-first, deadline dates soonest-first, names and the
 * pipeline A-to-Z / front-to-back. Only picking a FIELD applies these; the
 * direction control then overrides freely, and a field with no entry keeps
 * whatever direction is already set.
 */
export const ISSUE_SORT_DEFAULT_DIRECTION: Partial<
  Record<string, SortDirection>
> = {
  // Clearing re-seats the resting direction: unsorted, the direction is
  // inert, and a stray `desc` left behind would read as an unsaved edit.
  none: "asc",
  name: "asc",
  stage: "asc",
  assignee: "asc",
  startDate: "desc",
  targetDate: "asc",
  creationDate: "desc",
  lastUpdateDate: "desc",
  ageDate: "asc",
};

/**
 * The muted "no value" label per dimension, for group/column headers. The
 * entity kinds are capitalized because they are product nouns — the same
 * Stage / Workflow / Team a settings page, a breadcrumb and a filter chip
 * name. `assignee` is a role word, not a kind, so it stays lowercase.
 */
export const UNASSIGNED_DIMENSION_LABEL: Record<IssueGroupDimension, string> = {
  stage: "No Stage",
  workflow: "No Workflow",
  assignee: "No assignee",
  team: "No Team",
  teams: "No Teams",
  // Updates-off is the null status (see AGE_GROUPS) — the same word the card
  // ghost and the filter option use, so a band never renames the state.
  age: AGE_STATUS_CONFIG.disabled.label,
  issueType: "No Type",
};
